import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Trophy, Users, Flag, Plus, ChevronRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const [stats, setStats] = useState({ championships: 0, pilots: 0 });
  const [recentChampionships, setRecentChampionships] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token 
          ? { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } 
          : { 'Content-Type': 'application/json' };
        
        // Fetch championships
        const champsRes = await fetch(`${API}/championships`, { 
          method: 'GET',
          credentials: 'include', 
          headers 
        });
        
        if (champsRes.ok) {
          const champs = await champsRes.json();
          setStats(prev => ({ ...prev, championships: champs.length }));
          setRecentChampionships(champs.slice(0, 5));
        }
        
        // Pilots endpoint is admin-only, so we skip it for non-admins
        // The count will just show 0 for delegates
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="dashboard-page">
        {/* Welcome Section */}
        <div 
          className="relative rounded-lg overflow-hidden"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1565544843827-187614119ea7?w=1200&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-black/70" />
          <div className="relative z-10 p-6 lg:p-8">
            <h1 className="font-heading font-black text-2xl lg:text-3xl uppercase mb-2">
              Panel de Control
            </h1>
            <p className="text-zinc-400 max-w-xl">
              Gestiona tus campeonatos AECAR, pilotos y clasificaciones desde aquí.
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={<Trophy className="w-6 h-6" />}
            label="Campeonatos"
            value={loading ? <Loader2 className="w-5 h-5 animate-spin" /> : stats.championships}
            color="primary"
          />
          <StatCard
            icon={<Users className="w-6 h-6" />}
            label="Pilotos"
            value={loading ? <Loader2 className="w-5 h-5 animate-spin" /> : stats.pilots}
            color="secondary"
          />
          <StatCard
            icon={<Flag className="w-6 h-6" />}
            label="Carreras"
            value={loading ? <Loader2 className="w-5 h-5 animate-spin" /> : stats.championships * 4}
            color="primary"
          />
          <StatCard
            icon={<Trophy className="w-6 h-6" />}
            label="Públicos"
            value={loading ? <Loader2 className="w-5 h-5 animate-spin" /> : recentChampionships.filter(c => c.is_public).length}
            color="secondary"
          />
        </div>

        {/* Quick Actions */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Recent Championships */}
          <Card className="bg-card/50 border-white/10">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="font-heading text-lg uppercase">
                Campeonatos Recientes
              </CardTitle>
              <Button asChild variant="ghost" size="sm" data-testid="view-all-championships-btn">
                <Link to="/campeonatos">
                  Ver todos <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : recentChampionships.length === 0 ? (
                <div className="text-center py-8">
                  <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground mb-4">No hay campeonatos</p>
                  <Button asChild data-testid="create-first-championship-btn">
                    <Link to="/campeonatos">
                      <Plus className="w-4 h-4 mr-2" />
                      Crear Campeonato
                    </Link>
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {recentChampionships.map((champ) => (
                    <Link
                      key={champ.championship_id}
                      to={`/campeonatos/${champ.championship_id}`}
                      className="flex items-center justify-between p-3 rounded-md bg-black/20 hover:bg-black/40 transition-colors"
                      data-testid={`championship-${champ.championship_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Trophy className="w-5 h-5 text-primary" />
                        <div>
                          <p className="font-medium">{champ.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {champ.is_public ? 'Público' : 'Privado'}
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="bg-card/50 border-white/10">
            <CardHeader>
              <CardTitle className="font-heading text-lg uppercase">
                Acciones Rápidas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button asChild className="w-full justify-start" data-testid="quick-create-championship-btn">
                <Link to="/campeonatos">
                  <Trophy className="w-5 h-5 mr-3" />
                  Crear Nuevo Campeonato
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full justify-start border-white/10" data-testid="quick-manage-pilots-btn">
                <Link to="/pilotos">
                  <Users className="w-5 h-5 mr-3" />
                  Gestionar Pilotos
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Help Section */}
        <Card className="bg-card/50 border-white/10">
          <CardContent className="pt-6">
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
              <div className="flex-1">
                <h3 className="font-heading font-bold text-lg uppercase mb-1">
                  Sistema de Puntos AECAR
                </h3>
                <p className="text-sm text-muted-foreground">
                  4 carreras por campeonato. Escala de puntos del 1º (640 pts) al 90º (2 pts). 
                  Descarte automático del peor resultado. Desempate según Art. 18.1.
                </p>
              </div>
              <div className="text-sm font-mono bg-black/30 p-3 rounded-md">
                <p className="text-primary">1º: 640 | 2º: 613 | 3º: 587</p>
                <p className="text-muted-foreground">Asistencia: +150/+100/+50</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}

function StatCard({ icon, label, value, color }) {
  const colorClass = color === 'primary' ? 'text-primary' : 'text-secondary';
  return (
    <Card className="bg-card/50 border-white/10">
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div className={`${colorClass}`}>{icon}</div>
          <div>
            <p className="text-2xl font-mono font-bold">{value}</p>
            <p className="text-xs text-muted-foreground uppercase">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
